/**
 * LeaseDetailPanel — Shell component for lease detail view.
 *
 * Supports two data paths:
 *   1. `lease` prop — direct LeaseTableRow (from LeasesPage table)
 *   2. `leaseId` prop — fetches full row via useLeaseById (from ramp view)
 *
 * Renders as Dialog (modal) or Sheet (sidebar) based on persisted preference.
 * Hosts LeaseMutateDrawer for renewal and lifecycle dialogs (Terminate, Cancel, Complete).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useLeaseViewStore } from '@/stores/leaseViewStore'
import { useLeaseById, useContractAircraft, useTerminateLease, useCancelLease, useCompleteLease } from '@/hooks/useLeases'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { LeaseMutateDrawer } from './LeaseMutateDrawer'
import { LeaseDetailContent } from './LeaseDetailContent'
import { TerminateLeaseDialog } from './TerminateLeaseDialog'
import { CancelLeaseDialog } from './CancelLeaseDialog'
import { CompleteLeaseDialog } from './CompleteLeaseDialog'
import type { LeaseTableRow, RenewalStatus } from '@/types/leases'

// ============================================
// Props
// ============================================

interface LeaseDetailPanelProps {
  /** Direct data — used by LeasesPage (no fetch needed) */
  lease?: LeaseTableRow | null
  /** Lease ID to fetch — used by ramp view */
  leaseId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit: (id: string) => void
  /** Check out allocation (ramp view only — sets allocation to completed/departed) */
  onCheckOut?: () => void
  isCheckOutLoading?: boolean
}

// ============================================
// Loading Skeleton
// ============================================

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-5 w-24" />
      </div>
      <Skeleton className="h-px w-full" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <div className="space-y-1 pl-6">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function LeaseDetailPanel({
  lease: leaseProp,
  leaseId,
  open,
  onOpenChange,
  onEdit,
  onCheckOut,
  isCheckOutLoading,
}: LeaseDetailPanelProps) {
  const { detailPanelMode, setDetailPanelMode } = useLeaseViewStore()
  const { locationId: ctxLocationId, scope } = useAppContext()
  const navigate = useNavigate()

  // Fetch full row when only leaseId is provided
  const resolvedId = leaseProp?.id ?? leaseId
  const { data: fetchedLease, isLoading } = useLeaseById(
    !leaseProp && leaseId ? leaseId : undefined
  )

  const lease = leaseProp ?? fetchedLease ?? null

  // Fetch contract aircraft
  const { data: aircraft } = useContractAircraft(lease?.id)

  // Renewal drawer
  const [renewalDrawerOpen, setRenewalDrawerOpen] = useState(false)

  // Lifecycle dialogs
  const [terminateOpen, setTerminateOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const terminateMutation = useTerminateLease()
  const cancelMutation = useCancelLease()
  const completeMutation = useCompleteLease()

  const handleNavigateToClient = (clientName: string) => {
    navigate(`/clients?search=${encodeURIComponent(clientName)}`)
  }

  const handleNavigateToMap = (unitId: string) => {
    const slug = scope === 'location' && ctxLocationId
      ? window.location.pathname.split('/')[2]
      : undefined
    if (slug) {
      navigate(`/${slug}/ramp?unit=${unitId}`)
    } else {
      navigate(`/ramp?unit=${unitId}`)
    }
  }

  const handleToggleMode = () => {
    setDetailPanelMode(
      detailPanelMode === 'sidePanel' ? 'modal' : 'sidePanel'
    )
  }

  const handleEdit = () => {
    if (lease) onEdit(lease.id)
  }

  const handleRenew = () => {
    setRenewalDrawerOpen(true)
  }

  const handleTerminate = () => setTerminateOpen(true)
  const handleCancel = () => setCancelOpen(true)
  const handleComplete = () => setCompleteOpen(true)

  const confirmTerminate = (params: { termination_reason: string; termination_date: string }) => {
    if (!lease) return
    terminateMutation.mutate(
      { id: lease.id, ...params },
      {
        onSuccess: () => {
          setTerminateOpen(false)
          toast.success(`Lease ${lease.contract_number} terminated`)
        },
      }
    )
  }

  const confirmCancel = (params: { cancellation_reason?: string }) => {
    if (!lease) return
    cancelMutation.mutate(
      { id: lease.id, cancellation_reason: params.cancellation_reason },
      {
        onSuccess: () => {
          setCancelOpen(false)
          toast.success(`Lease ${lease.contract_number} cancelled`)
        },
      }
    )
  }

  const confirmComplete = () => {
    if (!lease) return
    completeMutation.mutate(lease.id, {
      onSuccess: () => {
        setCompleteOpen(false)
        toast.success(`Lease ${lease.contract_number} completed`)
      },
    })
  }

  // Nothing to show
  if (!resolvedId && !lease) return null

  // Build content (or skeleton while loading)
  const content =
    !lease && isLoading ? (
      <DetailSkeleton />
    ) : lease ? (
      <LeaseDetailContent
        lease={lease}
        aircraft={aircraft}
        panelMode={detailPanelMode}
        onToggleMode={handleToggleMode}
        onEdit={handleEdit}
        onRenew={handleRenew}
        onTerminate={handleTerminate}
        onCancel={handleCancel}
        onComplete={handleComplete}
        onCheckOut={onCheckOut}
        isCheckOutLoading={isCheckOutLoading}
        onNavigateToClient={handleNavigateToClient}
        onNavigateToMap={handleNavigateToMap}
      />
    ) : null

  const lockedLocationId = scope === 'location' && ctxLocationId ? ctxLocationId : undefined

  return (
    <>
      {detailPanelMode === 'modal' ? (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="sr-only">Lease Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">{content}</div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent className="sm:max-w-md p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Lease Details</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-full">
              <div className="space-y-4 p-6">{content}</div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      {/* Renewal Drawer */}
      {lease && (
        <LeaseMutateDrawer
          open={renewalDrawerOpen}
          onOpenChange={setRenewalDrawerOpen}
          renewalLeaseId={lease.id}
          lockedLocationId={lockedLocationId}
        />
      )}

      {/* Lifecycle Dialogs */}
      {lease && (
        <>
          <TerminateLeaseDialog
            open={terminateOpen}
            onOpenChange={setTerminateOpen}
            contractNumber={lease.contract_number}
            onConfirm={confirmTerminate}
            isPending={terminateMutation.isPending}
          />
          <CancelLeaseDialog
            open={cancelOpen}
            onOpenChange={setCancelOpen}
            contractNumber={lease.contract_number}
            onConfirm={confirmCancel}
            isPending={cancelMutation.isPending}
          />
          <CompleteLeaseDialog
            open={completeOpen}
            onOpenChange={setCompleteOpen}
            contractNumber={lease.contract_number}
            onConfirm={confirmComplete}
            isPending={completeMutation.isPending}
          />
        </>
      )}
    </>
  )
}

/**
 * Get renewal CTA label based on renewal status
 */
export function getRenewalCTALabel(
  renewalStatus: RenewalStatus | null
): string | null {
  switch (renewalStatus) {
    case 'not_started':
      return 'Start Renewal'
    case 'offer_sent':
      return 'Follow Up'
    case 'negotiating':
      return 'View Details'
    case 'signed':
      return 'Activate'
    default:
      return null
  }
}
